import ArrSVG from '@public/icons/btn-arr.svg';
import classNames from 'classnames';
import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { Navigation } from 'swiper';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';
import { Page } from '@/entities/page';
import Pagination from '@/shared/components/shared/Pagination';
import { useMounted } from '@/shared/hooks/use-mounted';
import { useOverflowing } from '@/shared/hooks/use-overflowing';
import { useTypograf } from '@/shared/hooks/use-typograf';
import Link from '../../shared/Link';

export type ServicesSectionProps = {
    title: string;
    subtitle: string;
    cards: Page[];
};

const SECTION_ID = 'services';

type Props = React.HTMLAttributes<HTMLElement> & {
    servicesSectionProps: ServicesSectionProps;
};

function ServicesSection({ servicesSectionProps, ...props }: Props) {
    const sliderRef = useRef<SwiperRef>(null);
    const [lastRealIndex, setLastRealIndex] = useState(0);
    const [currentRealIndex, setCurrentRealIndex] = useState(0);
    const numSlides = servicesSectionProps.cards.length;
    const tpTitle = useTypograf(servicesSectionProps.title);
    const tpSubtitle = useTypograf(servicesSectionProps.subtitle);

    const slider = useRef<HTMLDivElement | null>(null);
    const isInitialized = useOverflowing(slider);

    const isMounted = useMounted();

    return (
        <section
            {...props}
            className={classNames('services-section js-header-theme-trigger', props.className)}
            id={props.id ?? SECTION_ID}
            data-theme="dark"
        >
            <div className="services-top wrapper">
                <div className="services-title h3">{tpTitle}</div>
                <div className="services-description-block">
                    <div className="services-description text-s">{tpSubtitle}</div>
                </div>
            </div>
            <div className="services-bottom">
                {isMounted && isInitialized ? (
                    <>
                        <Swiper
                            ref={sliderRef as MutableRefObject<SwiperRef>}
                            className={classNames('services-slider', { 'is-enabled': isInitialized })}
                            modules={[Navigation]}
                            speed={650}
                            slidesPerView={'auto'}
                            breakpoints={{
                                320: {
                                    spaceBetween: 8,
                                    slidesOffsetBefore: 16,
                                    slidesOffsetAfter: 16,
                                },
                                769: {
                                    spaceBetween: 12,
                                    slidesOffsetBefore: 22,
                                    slidesOffsetAfter: 22,
                                },
                                1201: {
                                    spaceBetween: 20,
                                    slidesOffsetBefore: 36,
                                    slidesOffsetAfter: 36,
                                },
                                1366: {
                                    spaceBetween: 16,
                                    slidesOffsetBefore: 70,
                                    slidesOffsetAfter: 70,
                                },
                            }}
                            onSlideChange={(swiper) => {
                                const newRealIndex = swiper.realIndex;
                                setLastRealIndex(currentRealIndex);
                                setCurrentRealIndex(newRealIndex);
                            }}
                        >
                            {servicesSectionProps.cards.map((slide, i) => (
                                <SwiperSlide className="services-slider-slide" key={i}>
                                    <Link href={`/services/${slide.slug}/`} className="services-card">
                                        <div className="services-card-text">
                                            <div className="services-card-title text-xl">{slide.name}</div>
                                            <div className="services-card-description text-s">{slide.description}</div>
                                        </div>
                                        <div className="services-card-btn">
                                            <ArrSVG />
                                        </div>
                                    </Link>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                        {isInitialized && (
                            <Pagination
                                numSlides={numSlides}
                                currentRealIndex={currentRealIndex}
                                lastRealIndex={lastRealIndex}
                                prevElClass="services-slider-prev-btn"
                                nextElClass="services-slider-next-btn"
                                wrapperClass="services-pagination-wrapper wrapper"
                                onPrevClick={() => {
                                    sliderRef.current?.swiper.slidePrev();
                                }}
                                onNextClick={() => {
                                    sliderRef.current?.swiper.slideNext();
                                }}
                            />
                        )}
                    </>
                ) : (
                    <div className="services__list wrapper swiper__wrapper" ref={slider}>
                        {servicesSectionProps.cards.map((card, i) => (
                            <Link
                                href={'/services/' + card.slug}
                                className="services-card services-card__slide"
                                key={i}
                            >
                                <div className="services-card-text">
                                    <div className="services-card-title text-xl">{card.name}</div>
                                    <div className="services-card-description text-s">{card.description}</div>
                                </div>
                                <div className="services-card-btn">
                                    <ArrSVG />
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}

export default ServicesSection;
